import { supabase } from "@/integrations/supabase/client";
import {
  mergeTrendNotifySettings,
  parseHistory,
  readTrendNotifySettings,
  setTrendNotifyRemoteWriter,
  writeTrendNotifySettings,
  type TrendNotifySettings,
} from "@/lib/trendNotifications";

const COLUMNS =
  "trend_notify_mode, trend_notify_last_summary_at, trend_notify_pending, trend_seen_ids, trend_unread_ids, trend_notify_history, trend_notify_email, trend_notify_push";

/** Maps profile columns onto the client settings shape. */
export function fromRow(row: any): Partial<TrendNotifySettings> {
  return {
    mode: row?.trend_notify_mode ?? undefined,
    lastSummaryAt: row?.trend_notify_last_summary_at
      ? new Date(row.trend_notify_last_summary_at).getTime()
      : undefined,
    pending: row?.trend_notify_pending ?? [],
    seenIds: row?.trend_seen_ids ?? [],
    unreadIds: row?.trend_unread_ids ?? [],
    history: parseHistory(row?.trend_notify_history),
    email: typeof row?.trend_notify_email === "boolean" ? row.trend_notify_email : undefined,
    push: typeof row?.trend_notify_push === "boolean" ? row.trend_notify_push : undefined,
  };
}

/** Loads the profile copy and merges it into the local copy (remote mode wins). */
export async function loadTrendNotifySettings(): Promise<TrendNotifySettings> {
  const local = readTrendNotifySettings();
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return local;
    const { data } = await supabase
      .from("user_preferences")
      .select(COLUMNS)
      .eq("user_id", auth.user.id)
      .maybeSingle();
    if (!data) return local;
    const merged = mergeTrendNotifySettings(local, fromRow(data));
    writeTrendNotifySettings(merged, { remote: false });
    return merged;
  } catch {
    return local;
  }
}

/* ------------------------------------------------------------------ *
 * Background write queue (optimistic UI)
 * ------------------------------------------------------------------ */

const MAX_ATTEMPTS = 4;
let inFlight = false;
/** Newest settings still waiting to reach the backend. */
let queued: TrendNotifySettings | null = null;
let syncing = false;

export const TREND_NOTIFY_SYNC_EVENT = "td:trend-notify-sync";

function emitSyncState(pending: boolean, failed = false) {
  if (syncing === pending && !failed) return;
  syncing = pending;
  try {
    window.dispatchEvent(
      new CustomEvent(TREND_NOTIFY_SYNC_EVENT, { detail: { pending, failed } }),
    );
  } catch {
    /* noop */
  }
}

async function writeRow(next: TrendNotifySettings): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return;
  const { error } = await supabase.from("user_preferences").upsert(
    {
      user_id: auth.user.id,
      trend_notify_mode: next.mode,
      trend_notify_last_summary_at: next.lastSummaryAt
        ? new Date(next.lastSummaryAt).toISOString()
        : null,
      trend_notify_pending: next.pending,
      trend_seen_ids: next.seenIds,
      trend_unread_ids: next.unreadIds,
      trend_notify_history: next.history as unknown as any,
      trend_notify_email: next.email,
      trend_notify_push: next.push,
    },
    { onConflict: "user_id" },
  );
  if (error) throw new Error(error.message);
}

/**
 * Mirrors the settings to the profile row in the background. The local copy is
 * already applied, so failures retry with backoff and only the newest snapshot
 * is ever written (later edits supersede earlier ones).
 */
export async function pushTrendNotifySettings(next: TrendNotifySettings): Promise<void> {
  queued = next;
  if (inFlight) return;
  inFlight = true;
  emitSyncState(true);
  try {
    while (queued) {
      const snapshot = queued;
      queued = null;
      let attempt = 0;
      for (;;) {
        try {
          await writeRow(snapshot);
          break;
        } catch {
          attempt += 1;
          if (attempt >= MAX_ATTEMPTS || queued) {
            if (attempt >= MAX_ATTEMPTS) emitSyncState(false, true);
            break;
          }
          await new Promise((r) => setTimeout(r, 400 * 2 ** (attempt - 1)));
        }
      }
    }
  } finally {
    inFlight = false;
    emitSyncState(false);
  }
}

/** Wires writeTrendNotifySettings() up to the profile mirror. */
export function registerTrendNotifyRemote(): void {
  setTrendNotifyRemoteWriter((settings) => {
    void pushTrendNotifySettings(settings);
  });
}

/**
 * Cross-device sync: any change to this user's preferences row is merged into
 * the local copy, so seen/dismissed state converges everywhere.
 */
export function subscribeToTrendNotifyRow(
  userId: string,
  onRemote: (settings: TrendNotifySettings) => void,
): () => void {
  const channel = supabase
    .channel(`trend-notify-${userId}:${Math.random().toString(36).slice(2)}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_preferences",
        filter: `user_id=eq.${userId}`,
      },
      (payload: any) => {
        const merged = mergeTrendNotifySettings(
          readTrendNotifySettings(),
          fromRow(payload.new),
        );
        writeTrendNotifySettings(merged, { remote: false });
        onRemote(merged);
      },
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
